import React, { useEffect } from 'react';
import {
  Title,
  Text,
  Label,
  Select,
  Option,
  Switch,
  Input,
  Button,
  MessageStrip,
  Icon,
} from '@ui5/webcomponents-react';
import useAutoTranslate from '../../../hooks/useAutoTranslate';
import { CONFIG_STYLES } from '../configStyles';

function ApplicationConfig({ formData, setFormData, systemSettings, onSave, isLoading }) {
  const applicationSettings = systemSettings?.APPLICATION;
  const { autoT } = useAutoTranslate();

  useEffect(() => {
    console.log('ApplicationConfig - Current Values:', { formData, applicationSettings });
  }, [formData, applicationSettings]);

  if (!formData || !applicationSettings) {
    return <div>Error: Faltan datos necesarios para la configuración de la aplicación</div>;
  }

  const handleInputChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const prepareDataForSave = () => ({
    ...applicationSettings,
    theme:          { ...applicationSettings.theme,          default: formData.theme },
    language:       { ...applicationSettings.language,       default: formData.language },
    sessionTimeout: { ...applicationSettings.sessionTimeout, default: formData.sessionTimeout },
    pageSize:       { ...applicationSettings.pageSize,       default: formData.pageSize },
    notifications:  { ...applicationSettings.notifications,  default: formData.notifications },
    autoSave:       { ...applicationSettings.autoSave,       default: formData.autoSave },
  });

  const handleSave = () => onSave(prepareDataForSave());

  const hasChanges = () =>
    formData.theme          !== applicationSettings.theme.default          ||
    formData.language       !== applicationSettings.language.default       ||
    formData.sessionTimeout !== applicationSettings.sessionTimeout.default ||
    formData.pageSize       !== applicationSettings.pageSize.default       ||
    formData.notifications  !== applicationSettings.notifications.default  ||
    formData.autoSave       !== applicationSettings.autoSave.default;

  const renderSelect = (field, options, currentValue) => (
    <Select
      value={currentValue}
      style={{ width: '100%' }}
      onChange={(e) => {
        const selectedValue =
          e.detail.selectedOption.getAttribute('data-key') || e.detail.selectedOption.value;
        handleInputChange(field, selectedValue);
      }}
    >
      {options.map(option => (
        <Option key={option.value} data-key={option.value} selected={option.value === currentValue}>
          {option.label}
        </Option>
      ))}
    </Select>
  );

  return (
    <div style={CONFIG_STYLES.page}>
      {/* Header */}
      <div style={CONFIG_STYLES.headerRow}>
        <div style={CONFIG_STYLES.headerTitle}>
          <Title level="H3">{autoT('Configuración de Aplicación')}</Title>
          <Text style={CONFIG_STYLES.labelHint}>{autoT('Personaliza la experiencia de usuario y comportamiento')}</Text>
        </div>
        <Button
          design="Emphasized"
          onClick={handleSave}
          disabled={!hasChanges() || isLoading}
          icon="save"
        >
          {isLoading ? autoT('Guardando...') : autoT('Guardar Cambios')}
        </Button>
      </div>

      {hasChanges() && (
        <MessageStrip design="Information" style={{ marginBottom: '1rem' }}>
          {autoT('Hay cambios pendientes de guardar')}
        </MessageStrip>
      )}

      {/* Selects */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Preferencias de Interfaz')}</Title>
        </div>
        <div style={CONFIG_STYLES.formGrid}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Tema de la Aplicación')}</Label>
            {renderSelect('theme', applicationSettings.theme.options, formData.theme)}
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Idioma')}</Label>
            {renderSelect('language', applicationSettings.language.options, formData.language)}
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Tiempo de Sesión (minutos)')}</Label>
            <Input
              type="Number"
              value={formData.sessionTimeout}
              style={{ width: '100%' }}
              onChange={(e) => handleInputChange('sessionTimeout', e.target.value)}
              min={applicationSettings.sessionTimeout.min}
              max={applicationSettings.sessionTimeout.max}
            />
          </div>

          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Elementos por Página')}</Label>
            {renderSelect('pageSize', applicationSettings.pageSize.options, formData.pageSize)}
          </div>
        </div>
      </div>

      {/* Switches */}
      <div style={CONFIG_STYLES.sectionCard}>
        <div style={CONFIG_STYLES.sectionDivider}>
          <Title level="H4">{autoT('Opciones de Comportamiento')}</Title>
        </div>

        <div style={CONFIG_STYLES.switchRow}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Notificaciones')}</Label>
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Recibir notificaciones del sistema')}</Text>
          </div>
          <Switch
            checked={formData.notifications}
            onChange={(e) => handleInputChange('notifications', e.target.checked)}
          />
        </div>

        <div style={CONFIG_STYLES.switchRowLast}>
          <div style={CONFIG_STYLES.fieldGroup}>
            <Label>{autoT('Guardado Automático')}</Label>
            <Text style={CONFIG_STYLES.labelHint}>{autoT('Guardar cambios automáticamente')}</Text>
          </div>
          <Switch
            checked={formData.autoSave}
            onChange={(e) => handleInputChange('autoSave', e.target.checked)}
          />
        </div>
      </div>

      {/* Info */}
      <div style={CONFIG_STYLES.infoBox}>
        <div style={CONFIG_STYLES.infoHeader}>
          <Icon name="information" style={{ color: 'var(--sapHighlightColor)' }} />
          <Title level="H5">{autoT('Información')}</Title>
        </div>
        <ul style={{ color: 'var(--sapContent_LabelColor)', margin: 0, paddingLeft: '1.25rem', lineHeight: '1.8' }}>
          <li><Text>{autoT('Los cambios en el tema se aplicarán inmediatamente')}</Text></li>
          <li><Text>{autoT('El cambio de idioma requiere recargar la página')}</Text></li>
          <li><Text>{autoT('El tiempo de sesión se aplica en el próximo inicio de sesión')}</Text></li>
        </ul>
      </div>
    </div>
  );
}

export default ApplicationConfig;
